const readElevation = (point) => {
  const value = Number(point?.ele ?? point?.elevation ?? point?.alt ?? point?.altitude);
  return Number.isFinite(value) ? value : null;
};

export const TRACK_ELEVATION_STATS_OPTIONS = {
  minDelta: 5,
  maxDelta: 30,
  maxVerticalSpeed: 0.7,
  minVerticalAllowance: 5,
  smoothingWindow: 5,
};

function smoothElevationPoints(points, opts = {}) {
  const windowSize = Math.max(1, Math.round(Number(opts.smoothingWindow ?? 1)));
  if (!Array.isArray(points) || points.length < 2 || windowSize <= 1) return points;
  const half = Math.floor(windowSize / 2);
  return points.map((point, index) => {
    const vals = [];
    for (let i = index - half; i <= index + half; i += 1) {
      if (i < 0 || i >= points.length) continue;
      const elevation = readElevation(points[i]);
      if (Number.isFinite(elevation)) vals.push(elevation);
    }
    if (!vals.length) return point;
    vals.sort((a, b) => a - b);
    const mid = Math.floor(vals.length / 2);
    const ele =
      vals.length % 2 === 1 ? vals[mid] : (vals[mid - 1] + vals[mid]) / 2;
    return { ...point, ele };
  });
}

export function calculateElevationStats(points, opts = {}) {
  const minDelta = Math.max(0, Number(opts.minDelta ?? 1.5));
  const maxDelta = Number.isFinite(Number(opts.maxDelta))
    ? Math.max(minDelta, Number(opts.maxDelta))
    : Infinity;
  const maxVerticalSpeed = Number.isFinite(Number(opts.maxVerticalSpeed))
    ? Math.max(0, Number(opts.maxVerticalSpeed))
    : Infinity;
  const minVerticalAllowance = Math.max(
    minDelta,
    Number(opts.minVerticalAllowance ?? 5)
  );
  let gain = 0;
  let loss = 0;
  let min = Infinity;
  let max = -Infinity;
  let acceptedElevation = null;
  let acceptedTs = null;
  let validPoints = 0;

  for (const point of Array.isArray(points) ? points : []) {
    const elevation = readElevation(point);
    if (!Number.isFinite(elevation)) continue;

    let updateAcceptedElevation = acceptedElevation == null;
    if (acceptedElevation != null) {
      const diff = elevation - acceptedElevation;
      const ts = Number(point?.ts ?? point?.time);
      let isPlausible = Math.abs(diff) <= maxDelta;
      if (
        isPlausible &&
        Number.isFinite(ts) &&
        Number.isFinite(acceptedTs) &&
        ts > acceptedTs &&
        Number.isFinite(maxVerticalSpeed)
      ) {
        const allowance = Math.max(
          minVerticalAllowance,
          ((ts - acceptedTs) / 1000) * maxVerticalSpeed
        );
        isPlausible = Math.abs(diff) <= allowance;
      }
      if (!isPlausible) {
        // sanitizeElevationPoints() already removes isolated spikes. A jump
        // remaining here is therefore a sustained GPS altitude baseline
        // change. Rebase without counting the jump, otherwise all following
        // samples remain locked out until they return near the old altitude.
        validPoints += 1;
        min = Math.min(min, elevation);
        max = Math.max(max, elevation);
        acceptedElevation = elevation;
        if (Number.isFinite(ts)) acceptedTs = ts;
        continue;
      }
      if (diff >= minDelta) {
        gain += diff;
        updateAcceptedElevation = true;
      } else if (diff <= -minDelta) {
        loss += -diff;
        updateAcceptedElevation = true;
      }
    }

    validPoints += 1;
    min = Math.min(min, elevation);
    max = Math.max(max, elevation);
    if (updateAcceptedElevation) {
      acceptedElevation = elevation;
      const ts = Number(point?.ts ?? point?.time);
      if (Number.isFinite(ts)) acceptedTs = ts;
    }
  }

  if (validPoints === 0) {
    return { gain: 0, loss: 0, min: null, max: null, validPoints: 0 };
  }

  return { gain, loss, min, max, validPoints };
}

export function calculateTrackElevationStats(points, opts = {}) {
  const options = {
    ...TRACK_ELEVATION_STATS_OPTIONS,
    ...opts,
  };
  const sanitized = sanitizeElevationPoints(points, options);
  const smoothed = smoothElevationPoints(sanitized, options);
  return calculateElevationStats(smoothed, options);
}

export function sanitizeElevationPoints(points, opts = {}) {
  const maxDelta = Math.max(1, Number(opts.maxDelta ?? 30));
  const maxVerticalSpeed = Math.max(0, Number(opts.maxVerticalSpeed ?? 0.7));
  const minVerticalAllowance = Math.max(
    1,
    Number(opts.minVerticalAllowance ?? 5)
  );
  let acceptedElevation = null;
  let acceptedTs = null;
  let pending = null;
  const result = [];

  const isPlausibleStep = (fromElevation, fromTs, toElevation, toTs) => {
    const diff = Math.abs(toElevation - fromElevation);
    if (diff > maxDelta) return false;
    const hasElapsed =
      Number.isFinite(fromTs) && Number.isFinite(toTs) && toTs > fromTs;
    if (!hasElapsed) return true;
    const elapsedSec = (toTs - fromTs) / 1000;
    const allowance = Math.max(
      minVerticalAllowance,
      elapsedSec * maxVerticalSpeed
    );
    return diff <= allowance;
  };

  for (const point of Array.isArray(points) ? points : []) {
    const elevation = readElevation(point);
    if (!Number.isFinite(elevation)) {
      result.push(point);
      continue;
    }

    const ts = Number(point?.ts ?? point?.time);
    if (acceptedElevation != null) {
      if (!isPlausibleStep(acceptedElevation, acceptedTs, elevation, ts)) {
        if (
          pending &&
          isPlausibleStep(pending.elevation, pending.ts, elevation, ts)
        ) {
          // Two consecutive readings form a coherent new sequence. Restore
          // the first one and rebase so a single abrupt GPS altitude change
          // cannot flatten the rest of the recording into a long plateau.
          result[pending.resultIndex] = {
            ...result[pending.resultIndex],
            ele: pending.elevation,
          };
          pending = null;
          acceptedElevation = elevation;
          if (Number.isFinite(ts)) acceptedTs = ts;
          result.push({ ...point, ele: elevation });
          continue;
        }

        pending = {
          elevation,
          ts: Number.isFinite(ts) ? ts : null,
          resultIndex: result.length,
        };
        result.push({ ...point, ele: acceptedElevation });
        continue;
      }
    }

    pending = null;
    acceptedElevation = elevation;
    if (Number.isFinite(ts)) acceptedTs = ts;
    result.push({ ...point, ele: elevation });
  }

  return result;
}
